import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import moment from 'moment';
import {
  HeaderGroup,
  useFlexLayout,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import ReactTooltip from 'react-tooltip';

import useAppointment, { IAppointmentStatus } from 'hooks/useAppointment';
import ErrorIcon from 'components/ErrorIcon/ErrorIcon';
import RebookingPopover from 'components/RebookingPopover/RebookingPopover';
import RebookingWarningMessage from 'components/RebookingWarningMessage/RebookingWarningMessage';
import { ReactComponent as SortArrow } from 'assets/icons/sort_arrow.svg';
import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';
import { ReactComponent as RemovedInfo } from 'assets/icons/removed-info.svg';
import Pagination, { ITablePaginate } from 'components/Tables/Pagination';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';

import { loadAuthToken } from 'utils/storage';
import {
  capitalizeAvatarName,
  concatFullName,
  getErrorReason,
} from 'utils/common';
import { ERROR_REASON } from 'utils/constants';
import { formatFullPhoneNumber } from 'utils/formatPhoneNumber';
import { TRAFFIC_SOURCES } from 'pages/OnlineSchedulingReportPage/TrafficBySource/TRAFFIC_SOURCES';
import styles from './Table.module.css';
import PractitionerNameColumn from 'components/TableColumn/PractitionerNameColumn/PractitionerNameColumn';
import AppointmentDateTimeColumn from 'components/TableColumn/AppointmentDateTimeColumn/AppointmentDateTimeColumn';
import CSVScheduleInsights from '../CSVScheduleInsights/CSVScheduleInsights';
import ExcelExport from 'components/ExcelExport/ExcelExport';
import AppointmentDetailModal from 'components/AppointmentDetailModal/AppointmentDetailModal';

const NON_SORTABLE_COLUMNS = ['patientPhoneNumber'];

const defaultTablePaginationState = {
  currentPage: 1,
  pageSize: 10,
};

interface TableProps {
  clinicIds: string[];
  status: IAppointmentStatus;
  dateRange: { fromDate: string; toDate: string };
  sort: 'asc' | 'desc';
  setSort: Dispatch<SetStateAction<'asc' | 'desc'>>;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  shouldRun: boolean;
}

const Table: FC<TableProps> = ({
  clinicIds,
  status,
  dateRange,
  sort,
  setSort,
  sortBy,
  setSortBy,
  shouldRun,
}) => {
  const [pageSize, setPageSize] = useState(
    defaultTablePaginationState.pageSize
  );
  const [currentPage, setCurrentPage] = useState(
    defaultTablePaginationState.currentPage
  );

  const [selectedAppt, setSelectedAppt] = useState<{
    clinicId: string;
    appointmentId: string;
  } | null>(null);

  const { data, isLoading } = useAppointment({
    startDate: moment(dateRange.fromDate, 'YYYY-MM-DD')
      .startOf('days')
      .utc(true)
      .format(),
    endDate: moment(dateRange.toDate, 'YYYY-MM-DD')
      .endOf('days')
      .utc(true)
      .format(),
    limit: pageSize,
    page: currentPage,
    sort,
    sortBy,
    status,
    clinicIds,
    // THIS IS TO BYPASS THE INTERCEPTOR
    axiosRequestConfig: {
      headers: {
        Authorization: `JWT ${loadAuthToken()?.accessToken}`,
      },
    },
    shouldRun,
  });

  const columns = useMemo(
    () => [
      {
        Header: 'Appointment Date & Time',
        accessor: (field: any) => {
          return <AppointmentDateTimeColumn data={field} />;
        },
        id: 'appointmentDate',
      },
      {
        Header: 'Patient',
        accessor: (field: any) => {
          return (
            <div className="flex gap-x-0.8 items-center mr-[20px]">
              {field.patient ? (
                <>
                  <div className="w-2.4 h-2.4 bg-magenta rounded-full flex items-center justify-center text-white text-[1rem] shrink-0">
                    {field.patient
                      ? capitalizeAvatarName(
                          concatFullName({
                            firstName: field.patient.firstName,
                            lastName: field.patient.lastName,
                          })
                        )
                      : {}}
                  </div>
                  <div className="words-break">{field.patient.name}</div>
                </>
              ) : (
                <div />
              )}
            </div>
          );
        },
        id: 'patientName',
      },
      {
        Header: 'Phone Number',
        accessor: (field: any) => {
          if (!field.patient?.phoneNumber) {
            return null;
          }

          return (
            <div className="words-break pr-5">
              {formatFullPhoneNumber(`+${field.patient.phoneNumber}`)}
            </div>
          );
        },
        id: 'patientPhoneNumber',
      },
      {
        Header: 'Practice',
        accessor: (field: any) => {
          return (
            <div className="words-break mr-[20px]">{field.clinic.name}</div>
          );
        },
        id: 'clinicName',
      },
      {
        Header: 'Practitioner',
        accessor: (field: any) => {
          const isInactiveDoctor =
            field.linkedAppointment?.doctor?.inactive || field.doctor.inactive;

          return (
            <div className="pr-5">
              <PractitionerNameColumn field={field} />
              {isInactiveDoctor && (
                <>
                  <RemovedInfo
                    className="inline-block ml-0.6"
                    data-for={`inactive-doctor-${field.id}`}
                    data-tip=""
                  />
                  <ReactTooltip
                    id={`inactive-doctor-${field.id}`}
                    place="bottom"
                    effect="solid"
                    className={`${styles.tooltip} shadow-tooltip`}
                  >
                    <span className="text-11 text-darkest-grey flex h-full items-center normal-case">
                      The practitioner assigned to this appointment has been
                      inactivated in the PMS.
                    </span>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
        id: 'doctorName',
      },
      {
        Header: 'Service',
        accessor: (field: any) => {
          const isServiceAvailable =
            !field.service.deletedAt &&
            field.service.isActive &&
            field.service.isAvailable;

          return (
            <div className="words-break pr-5">
              {field.service.name}
              {!isServiceAvailable && (
                <>
                  <RemovedInfo
                    className="inline-block ml-0.6"
                    data-for={field.service.id}
                    data-tip=""
                  />
                  <ReactTooltip
                    id={field.service.id}
                    place="bottom"
                    effect="solid"
                    className={`${styles.tooltip} shadow-tooltip`}
                  >
                    <span className="text-11 text-darkest-grey flex h-full items-center normal-case">
                      This service has been deactivated or removed
                    </span>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
        width: 200,
        id: 'serviceName',
      },
      {
        Header: 'Source',
        accessor: (field: any) => {
          const source = TRAFFIC_SOURCES.find(
            ({ key }) => key === field.source
          );

          return (
            <div className="words-break pr-5">{source?.label || '—'}</div>
          );
        },
        id: 'source',
      },
      {
        Header: 'Requested Time',
        accessor: (field: any) => {
          const createdAtByTz = moment(field.createdAt).tz(field.timezone);

          return (
            <span>
              {createdAtByTz.format('MMM DD, YYYY')}
              <br />
              {createdAtByTz.format('hh:mm A Z UTC')}
            </span>
          );
        },
        id: 'createdAt',
      },
      {
        id: 'error_management',
        width: 72,
        Cell: ({ row }: any) => {
          const errorReason = getErrorReason(row.original);
          const awaitingList = (row.original as any)
            .awaitingAppointmentsByPatient;

          return (
            <div className="flex gap-x-1.6">
              <ErrorIcon appointment={{ ...row.original, errorReason }} />
              {!!awaitingList?.length && (
                <ErrorIcon
                  appointment={{
                    ...row.original,
                    id: `${row.original.id}_rebooking`,
                    errorReason: ERROR_REASON.EXISTING_BOOKING_BY_PATIENT,
                    rebookingRecognitionDays:
                      awaitingList[0]?.rebookingRecognitionDays,
                  }}
                >
                  <RebookingPopover appointments={awaitingList} />
                </ErrorIcon>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const isSortableColumn = (columnId: string) =>
    columnId !== 'error_management' && !NON_SORTABLE_COLUMNS.includes(columnId);

  const dataTable = useMemo(() => {
    if (isLoading) {
      return { data: [], metadata: null };
    }
    return { data: data.data, metadata: data.metadata };
  }, [data?.data, data?.metadata, isLoading]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns,
        data: dataTable.data ? dataTable.data : [],
        manualPagination: true,
        pageCount: dataTable.metadata ? data.metadata.total : 0,
      },
      useSortBy,
      usePagination,
      useRowSelect,
      useFlexLayout
    );

  const handleSortColumn = (column: HeaderGroup<object>) => {
    if (!isSortableColumn(column.id)) return;
    setCurrentPage(1);
    setSortBy(column.id);
    if (column.id === sortBy) {
      return setSort((sort) => (sort === 'asc' ? 'desc' : 'asc'));
    }
    setSort('asc');
  };

  const handlePaginating = (data: ITablePaginate) => {
    setCurrentPage(data.perPage === pageSize ? data.currPage : 1);
    setPageSize(data.perPage);
  };

  const onApptSelected = (cell: any) => {
    setSelectedAppt({
      appointmentId: cell.row.original.id,
      clinicId: cell.row.original.clinic.id,
    });
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tbody>
          <tr>
            <td colSpan={6}>
              <div className="w-full h-[37rem] flex justify-center items-center">
                <LoadingSpinner className="all-child:fill-magenta" />
              </div>
            </td>
          </tr>
        </tbody>
      );
    }
    if (data.metadata.total === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan={6}>
              <div className="w-full h-[24rem] flex flex-col gap-y-1.6 justify-center items-center">
                <EmptyBucket />
                <span>No appointment available on this list</span>
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody {...getTableBodyProps()} className="text-darkest-grey">
        {page.map((row) => {
          prepareRow(row);
          const awaitingList = (row.original as any)
            .awaitingAppointmentsByPatient;

          return (
            <tr
              {...row.getRowProps()}
              className="bg-white border-b border-light-grey px-2 hover:bg-lightPink cursor-pointer relative"
              style={{
                ...(row.getRowProps().style || {}),
                minHeight: `calc(12rem + ${awaitingList.length * 50}px)`,
              }}
            >
              {row.cells.map((cell: any) => {
                return (
                  <td
                    onClick={() => onApptSelected(cell)}
                    className={`text-left p-0 py-1.9 ${
                      awaitingList?.length ? 'self-start pt-1.9' : 'my-auto'
                    }`}
                    {...cell.getCellProps()}
                    style={{
                      ...(cell.getCellProps().style || {}),
                      minHeight: `calc(12rem + ${awaitingList.length * 50}px)`,
                    }}
                  >
                    {cell.render('Cell')}
                  </td>
                );
              })}
              {awaitingList?.length > 0 && (
                <div className="absolute bottom-0.5 left-[9%] right-[9%] max-w-[80%] z-[1] flex flex-col gap-y-1">
                  {awaitingList.map((child: any) => (
                    <RebookingWarningMessage
                      key={`msg-${child.id}`}
                      appointment={child}
                    />
                  ))}
                </div>
              )}
            </tr>
          );
        })}
      </tbody>
    );
  };

  return (
    <>
      {selectedAppt?.clinicId && (
        <AppointmentDetailModal
          appointmentId={selectedAppt.appointmentId}
          onClose={() => setSelectedAppt(null)}
          isHiddenAcceptRejectOptions={true}
          axiosConfig={{ headers: { 'X-ClinicId': selectedAppt.clinicId } }}
        />
      )}
      <div className="flex flex-row gap-x-[1.6rem] absolute right-4 top-2.4">
        <CSVScheduleInsights
          appointmentStatus={status}
          clinicIds={clinicIds}
          dateRange={dateRange}
          sort={sort}
          sortBy={sortBy}
          isDataLoading={isLoading || !shouldRun}
        />
        <ExcelExport
          appointmentStatus={status}
          clinicIds={clinicIds}
          dateRange={dateRange}
          sort={sort}
          sortBy={sortBy}
          isDataLoading={isLoading || !shouldRun}
        />
      </div>
      <table {...getTableProps()} className="w-full text-14 leading-[2.1rem]">
        <thead className="font-bold">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} className="px-2">
              {headerGroup.headers.map((column) => {
                return (
                  <th
                    {...column.getHeaderProps()}
                    className="text-left p-0 py-1.9"
                  >
                    <div
                      className={`flex items-center gap-x-0.9${
                        isSortableColumn(column.id) ? ' cursor-pointer' : ''
                      }`}
                      onClick={() => handleSortColumn(column)}
                    >
                      <span className="uppercase text-dim-grey text-11 leading-[1.8rem]">
                        {column.render('Header')}
                      </span>

                      {isSortableColumn(column.id) && (
                        <span>
                          <SortArrow
                            className={`${
                              column.id === sortBy && 'fill-magenta'
                            } ${
                              column.id === sortBy &&
                              sort === 'asc' &&
                              'rotate-180'
                            } transition-all`}
                          />
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {renderTableBody()}
      </table>
      {dataTable.metadata && dataTable.metadata.total > 0 && (
        <div className="py-1.6">
          <Pagination
            isBorderNotIncluded
            handlePaginating={handlePaginating}
            data={{
              currPage: dataTable.metadata.page,
              perPage: dataTable.metadata.limit,
              total: dataTable.metadata.total,
            }}
          />
        </div>
      )}
    </>
  );
};

export default Table;
